import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { Sheet, SheetContent } from "../../components/ui/sheet/index.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import { CONTACT_SHEET_OVERLAY_MOTION_CLASS } from "../contacts/contacts-table-presentation.js";
import { SettingsSectionNav } from "./SettingsSectionNav.js";
import { SettingsSidebar } from "./SettingsSidebar.js";

type SettingsWorkspaceValue = {
  organizationId: number;
  role: OrganizationMemberRoleName;
  openMobileNav: () => void;
  mobileNavOpen: boolean;
};

const SettingsWorkspaceContext = createContext<SettingsWorkspaceValue | null>(null);

export function useSettingsWorkspace(): SettingsWorkspaceValue {
  const value = useContext(SettingsWorkspaceContext);
  if (!value) {
    throw new Error("useSettingsWorkspace must be used inside SettingsWorkspaceLayout");
  }
  return value;
}

export function SettingsWorkspaceLayout({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const location = useLocation();
  const reduceMotion = useReducedMotion() ?? false;
  const sectionKey =
    location.pathname === "/settings" || location.pathname === "/settings/"
      ? "/settings/general"
      : location.pathname;
  const previousSection = useRef<string | null>(null);
  const animateSection =
    previousSection.current !== null &&
    previousSection.current !== sectionKey &&
    !reduceMotion;
  previousSection.current = sectionKey;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const openMobileNav = useCallback((): void => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback((): void => setMobileNavOpen(false), []);

  const value = useMemo<SettingsWorkspaceValue>(
    () => ({ organizationId, role, openMobileNav, mobileNavOpen }),
    [organizationId, role, openMobileNav, mobileNavOpen],
  );

  return (
    <SettingsWorkspaceContext.Provider value={value}>
      <div
        className="flex h-full min-h-0 bg-canvas p-2 sm:p-3 lg:p-4"
        data-testid="settings-workspace"
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-line/60 bg-surface shadow-[0_4px_24px_-12px_rgb(17_35_33/0.08)]">
          <div className="shrink-0 bg-surface px-4 pt-4 md:px-8 md:pt-5">
            <div className="mx-auto w-full max-w-[68rem]">
              <SettingsSectionNav role={role} />
            </div>
          </div>
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="start"
              className="flex h-full max-h-dvh w-[min(100%,22rem)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg lg:w-[20rem]"
              overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
              data-testid="settings-mobile-nav-sheet"
            >
              <SettingsSidebar
                role={role}
                variant="drawer"
                collapsed={false}
                onNavigate={closeMobileNav}
                onClose={closeMobileNav}
              />
            </SheetContent>
          </Sheet>

          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            <AnimatePresence initial={false}>
              <motion.div
                key={sectionKey}
                className="absolute inset-0 flex min-h-0 flex-col overflow-hidden"
                initial={animateSection ? { opacity: 0, y: 8 } : false}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </SettingsWorkspaceContext.Provider>
  );
}
